import React, { useRef, useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link, useNavigate } from 'react-router-dom';
import { advisoryCtaContent } from '../../data/mock';

gsap.registerPlugin(ScrollTrigger);

// All team members for carousel - matches Advisory page
export const teamMembersData = [
  {
    id: 1,
    name: 'Douglas Quezada',
    title: 'Chairman',
    image: '/images/douglas-quezada.jpeg'
  },
  {
    id: 2,
    name: 'Mohammed Alexander',
    title: 'Vice Chairman',
    image: '/images/mohammed-alexander.jpeg'
  },
  {
    id: 3,
    name: 'Richard Haws',
    title: 'Managing Director',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=400&fit=crop&crop=top'
  },
  {
    id: 4,
    name: 'Naru',
    title: 'CFO',
    image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=300&h=400&fit=crop&crop=top'
  },
  {
    id: 5,
    name: 'Richard Lee',
    title: 'Board Member',
    image: '/images/richard-lee.jpeg'
  },
  {
    id: 8,
    name: 'Usama Aziz',
    title: 'COO & Chief Legal Advisor',
    image: '/images/usama-aziz.jpeg'
  },
  {
    id: 9,
    name: 'Ali Sultan Al Hajri',
    title: 'MENA Senior Financial Advisor',
    image: '/images/ali-al-hajri.jpeg'
  },
  {
    id: 10,
    name: 'Ben Moufid Dekhil',
    title: 'VP, Business Development',
    image: '/images/ben-moufid-dekhil.jpeg'
  }
];

const BoardCarousel = () => {
  const sectionRef = useRef(null);
  const carouselRef = useRef(null);
  const animationRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.board-animate',
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.2,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Horizontal scroll animation - back and forth
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    let scrollPosition = 0;
    let direction = 1;
    const speed = 0.8;

    const animate = () => {
      const maxScroll = carousel.scrollWidth - carousel.clientWidth;
      scrollPosition += speed * direction;
      
      if (scrollPosition >= maxScroll) {
        direction = -1;
      } else if (scrollPosition <= 0) {
        direction = 1;
      }
      
      carousel.scrollLeft = scrollPosition;
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    const handleMouseEnter = () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };

    const handleMouseLeave = () => {
      animationRef.current = requestAnimationFrame(animate);
    };

    carousel.addEventListener('mouseenter', handleMouseEnter);
    carousel.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      carousel.removeEventListener('mouseenter', handleMouseEnter);
      carousel.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const handleMemberClick = (memberId) => {
    navigate(`/advisory?open=${memberId}`);
  };

  return (
    <section ref={sectionRef} className="py-24 bg-[#E8E4DC]">
      {/* Header */}
      <div className="l-container mb-12">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8">
          <h2 className="font-serif text-4xl lg:text-5xl text-[#0B1121] board-animate leading-tight">
            Meet the guiding<br />force behind Qasaba.
          </h2>
          <Link
            to={advisoryCtaContent.ctaLink}
            className="inline-flex items-center gap-2 text-[#0B1121] text-sm font-medium hover:text-[#4ECDC4] transition-colors board-animate group"
          >
            {advisoryCtaContent.cta}
            <span className="w-8 h-[1px] bg-current group-hover:w-12 transition-all duration-300" />
          </Link>
        </div>
      </div>

      {/* Photo Carousel */}
      <div 
        ref={carouselRef}
        className="flex gap-5 overflow-x-auto scrollbar-hide pl-[60px] pr-6 board-animate"
        style={{ scrollBehavior: 'auto' }}
      >
        {[...teamMembersData, ...teamMembersData].map((member, index) => (
          <div 
            key={`${member.id}-${index}`} 
            className="flex-shrink-0 w-[180px] lg:w-[200px] cursor-pointer group"
            onClick={() => handleMemberClick(member.id)}
          >
            <div className="aspect-[3/4] overflow-hidden rounded-lg mb-4 bg-[#1a2744]">
              <img
                src={member.image}
                alt={member.name}
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
              />
            </div>
            <p className="text-[#0B1121] text-sm font-medium leading-tight group-hover:text-[#4ECDC4] transition-colors">{member.name}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default BoardCarousel;
